import React, { useEffect, useRef, useState } from 'react';
import {
    Popover,
    Box,
    Typography,
    Button,
    Divider,
    IconButton,
    Collapse,
    CircularProgress
} from '@mui/material';
import { X } from 'lucide-react';
import capitalize from 'utils/capitalize';
import { formatAccessTime, GuestHelper } from 'utils/helpers/guest.helper';
import { userRoles } from 'utils/constants';
import UserInvitesService from 'services/user-invite.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import { DeclineInvitationModal } from './DeclineInvitationModal';
import { useAuth } from 'contexts/AuthContext';

const EXIT_DURATION = 450;
const TEAM_JOIN_RELOAD_DELAY = 1200;

// `rental_manager` -> `Rental Manager`: the org role key a team member is invited as.
const humanizeRoleKey = key =>
    String(key)
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase());

// What the invite makes the invitee at the property, for "… as a <label>". A role it does
// not know has no label rather than a guess.
const occupantRoleLabel = invite => {
    switch (invite.role) {
        case userRoles.TENANT:
            return 'tenant';
        case userRoles.ASSOCIATE:
            return 'associate';
        case userRoles.GUEST:
            return invite.type === 'vendor' ? 'vendor' : 'guest';
        default:
            return null;
    }
};

export function getInvitationDescription(invite) {
    if (!invite) return '';

    // A team member is i nvited to the organization, not to a property.
    if (invite.role === userRoles.OPERATOR) {
        const role = invite.type ? humanizeRoleKey(invite.type) : 'team member';

        return `has invited you to join ${invite.org_name || 'their organization'} as a ${role}.`;
    }

    const propertyName = invite?.property_address || 'their property';
    // Follows the sender's name: "<Iris Smith> from <Acme HOA> has invited you to …".
    const sender = invite.org_name ? `from ${invite.org_name} ` : '';
    const roleLabel = occupantRoleLabel(invite);
    const invitedTo = `${sender}has invited you to ${propertyName}${
        roleLabel ? ` as a ${roleLabel}` : ''
    }`;

    if (invite.role === userRoles.RESIDENT) {
        return `${sender}has invited you to join their household at ${propertyName}.`;
    }
    const hasRecurringAccess = invite?.access_days?.length > 0;

    const hasStartDate = !!invite?.access_begins;
    const hasEndDate = !!invite?.access_expires;

    const hasStartTime = !!invite?.access_start_time;
    const hasEndTime = !!invite?.access_end_time;

    if (
        !hasRecurringAccess &&
        !hasStartDate &&
        !hasEndDate &&
        !hasStartTime &&
        !hasEndTime
) {
        return `${invitedTo} with anytime access.`;
    }

    if (hasRecurringAccess) {
        if (hasStartTime && hasEndTime) {
            const start = formatAccessTime(invite.access_start_time);
            const end = formatAccessTime(invite.access_end_time);

            return `${invitedTo} with access on selected days from ${start} to ${end}.`;
        }

        return `${invitedTo} with access on selected days.`;
    }

    let message = invitedTo;

    if (hasStartDate) {
        message += ` starting ${GuestHelper.formatFirestoreTimestamp(
            invite.access_begins
        )}`;
    }

    if (hasEndDate) {
        message += ` until ${GuestHelper.formatFirestoreTimestamp(
            invite.access_expires
        )}`;
    }

    if (hasStartTime && hasEndTime) {
        message += ` from ${formatAccessTime(
            invite.access_start_time
        )} to ${formatAccessTime(invite.access_end_time)}`;
    }

    return `${message}.`;
}

// Brings the rows on screen (`shown`) in line with the live list (`incoming`): rows that are
// gone drop out unless they are still animating out (`exitingIds`), known rows keep their
// position, new ones go to the end.
export function mergeInvitations(shown, incoming, exitingIds) {
    const incomingById = new Map(incoming.map(inv => [inv.invite_id, inv]));
    const kept = shown
        .filter(
            inv => incomingById.has(inv.invite_id) || exitingIds.includes(inv.invite_id)
        )
        .map(inv => incomingById.get(inv.invite_id) || inv);
    const keptIds = new Set(kept.map(inv => inv.invite_id));

    return [...kept, ...incoming.filter(inv => !keptIds.has(inv.invite_id))];
}

// A failed check counts as still pending: the caller then reports the original error.
async function isStillPending(inviteId) {
    try {
        const pending = await UserInvitesService.getUserInvites();

        return pending.some(inv => inv.invite_id === inviteId);
    } catch (error) {
        return true;
    }
}

function InvitationItem({ invitation, onAccept, onDecline, processingAction }) {
    const isProcessing = Boolean(processingAction);
    const isAccepting = processingAction === 'accept';
    const isDeclining = processingAction === 'decline';
    const getName = () =>
        `${capitalize(invitation?.creator_first_name)} ${capitalize(
            invitation?.creator_last_name
        )}`;
    const getInitials = () =>
        `${invitation?.creator_first_name?.[0] || ''}${
            invitation?.creator_last_name?.[0] || ''
        }`.toUpperCase();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', p: '15px' }}>
            {/* Avatar + message */}
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#ecf1f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}
                >
                    <Typography
                        sx={{ fontSize: '13px', fontWeight: 500, color: '#596470' }}
                    >
                        {getInitials()}
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        fontSize: '13px',
                        color: '#212322',
                        lineHeight: '20px',
                        flex: 1
                    }}
                >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                        {getName()}
                    </Box>{' '}
                    <Box component="span" sx={{ fontWeight: 400 }}>
                        {getInvitationDescription(invitation)}
                    </Box>
                </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <Button
                    disabled={isProcessing}
                    onClick={() => onDecline(invitation.invite_id)}
                    variant="outlined"
                    color="secondary"
                    sx={{
                        height: 32,
                        minWidth: 110,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '13px',
                        color: '#212322',
                        bgcolor: 'white',
                        border: '1px solid #d9d9d9',
                        boxShadow: '0px 2px 0px rgba(0,0,0,0.02)',
                        px: '10px',
                        py: '5px',
                        '&:hover': {
                            borderColor: '#bfbfbf',
                            bgcolor: 'grey.50'
                        }
                    }}
                >
                    {isDeclining ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <CircularProgress
                                size={14}
                                thickness={5}
                                sx={{ color: '#D9D9D9' }}
                            />
                            Declining...
                        </Box>
                    ) : (
                        'Decline'
                    )}
                </Button>
                <Button
                    disabled={isProcessing}
                    onClick={() => onAccept(invitation.invite_id)}
                    variant="contained"
                    sx={{
                        height: 32,
                        minWidth: 110,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '13px',
                        color: 'white',
                        bgcolor: '#33c78f',
                        px: '10px',
                        py: '5px',
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#2eb880',
                            boxShadow: 'none',
                            color: 'white'
                        }
                    }}
                >
                    {isAccepting ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <CircularProgress
                                size={14}
                                thickness={5}
                                sx={{ color: '#D9D9D9' }}
                            />
                            Accepting...
                        </Box>
                    ) : (
                        'Accept'
                    )}
                </Button>
            </Box>
        </Box>
    );
}

export function InvitationsPanel({ anchorEl, onClose }) {
    const { userInvites, removeInvite, refreshAccessKeys, refreshUser } = useAuth();
    const [invitations, setInvitations] = useState(userInvites);
    const [exitingIds, setExitingIds] = useState([]);
    const exitingIdsRef = useRef(exitingIds);
    exitingIdsRef.current = exitingIds;
    const [processingById, setProcessingById] = useState({});
    const [declineInvite, setDeclineInvite] = useState(null);
    const declineModalOpen = Boolean(declineInvite);

    // `invitations` is what is on screen; `userInvites` (AuthContext) is the live list.
    // Follow it, but keep the rows still animating out and add new invites at the end so
    // an open panel does not shift under the user's cursor.
    useEffect(() => {
        setInvitations(prev => mergeInvitations(prev, userInvites, exitingIdsRef.current));
    }, [userInvites]);
    const startProcessing = (id, action) => {
        setProcessingById(prev => ({
            ...prev,
            [id]: action
        }));
    };

    const stopProcessing = id => {
        setProcessingById(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };
    const visibleInvitationCount = invitations.filter(
        inv => !exitingIds.includes(inv?.invite_id)
    ).length;

    const shouldShowEmptyState = visibleInvitationCount === 0;
    const removeInvitation = id => {
        setExitingIds(prev => [...prev, id]);

        setTimeout(() => {
            setInvitations(prev => prev.filter(inv => inv.invite_id !== id));
            removeInvite(id);
            setExitingIds(prev => prev.filter(exitingId => exitingId !== id));
        }, EXIT_DURATION);
    };

    // Accept/Decline on an invite that is no longer pending (withdrawn by the sender, answered
    // elsewhere) fails with 404/400. Resolves true once answered and false when the invite is
    // gone; any other failure is rethrown. The API is asked rather than the status code
    // guessed, because a 400 can also mean the invite belongs to someone else.
    const answerInvite = async (id, request) => {
        try {
            await request();

            return true;
        } catch (error) {
            if (await isStillPending(id)) {
                throw error;
            }

            return false;
        }
    };
    const dropUnavailableInvitation = id => {
        removeInvitation(id);
        SnackbarNotify.error('This invitation is no longer available');
    };

    const handleAccept = async id => {
        const invite = invitations.find(inv => inv.invite_id === id);

        try {
            startProcessing(id, 'accept');

            const accepted = await answerInvite(id, () =>
                UserInvitesService.accceptInvite(id)
            );

            if (!accepted) {
                dropUnavailableInvitation(id);

                return;
            }

            // Joining an org's team changes the account's org membership and role, which
            // only a fresh session picks up: the accept-invite page reloads for the same reason.
            // The reload goes to the root, not to the page the user is on: the accept can also move
            // their active org (when they were only a resident of the org they were in), and the
            // root opens the right app and a page it has.
            if (invite?.role === userRoles.OPERATOR) {
                removeInvitation(id);
                SnackbarNotify.success(
                    `You joined ${invite.org_name || 'the organization'}`
                );
                setTimeout(() => window.location.assign('/'), TEAM_JOIN_RELOAD_DELAY);

                return;
            }

            await refreshAccessKeys();

            removeInvitation(id);
            await refreshUser();
            SnackbarNotify.success('Added to access keys');
        } catch (error) {
            SnackbarNotify.error('Failed to accept invitation');
        } finally {
            stopProcessing(id);
        }
    };
    const handleDecline = id => {
        const invite = invitations.find(inv => inv.invite_id === id);
        setDeclineInvite(invite || null);
    };
    const handleConfirmDecline = async () => {
        const id = declineInvite?.invite_id;

        if (!id) return;

        try {
            startProcessing(id, 'decline');

            const declined = await answerInvite(id, () => UserInvitesService.denyInvite(id));

            setDeclineInvite(null);

            if (!declined) {
                dropUnavailableInvitation(id);

                return;
            }

            removeInvitation(id);
            SnackbarNotify.error('Invitation declined');
        } catch (error) {
            SnackbarNotify.error('Failed to decline invitation');
        } finally {
            stopProcessing(id);
        }
    };
    return (
        <>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        width: 330,
                        borderRadius: '12px',
                        border: '1px solid #f0f0f0',
                        boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        mt: '8px'
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pl: '20px',
                        pr: '15px',
                        height: 42
                    }}
                >
                    <Typography
                        sx={{ fontSize: '14px', fontWeight: 600, color: '#212322' }}
                    >
                        Manage invitations
                    </Typography>

                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{ color: '#262626', p: '4px' }}
                    >
                        <X size={16} strokeWidth={1.5} />
                    </IconButton>
                </Box>

                <Divider sx={{ borderColor: '#f0f0f0' }} />

                <Box sx={{ position: 'relative' }}>
                    {invitations.map((inv, idx) => {
                        const id = inv?.invite_id;
                        const isExiting = exitingIds.includes(id);

                        return (
                            <Collapse
                                key={id || idx}
                                in={!isExiting}
                                timeout={EXIT_DURATION}
                                unmountOnExit
                            >
                                <Box
                                    sx={{
                                        opacity: isExiting ? 0 : 1,
                                        transition: `opacity ${EXIT_DURATION}ms ease`
                                    }}
                                >
                                    <InvitationItem
                                        invitation={inv}
                                        processingAction={processingById[inv?.invite_id]}
                                        onAccept={handleAccept}
                                        onDecline={handleDecline}
                                    />

                                    {idx < invitations.length - 1 && (
                                        <Divider sx={{ borderColor: '#f0f0f0' }} />
                                    )}
                                </Box>
                            </Collapse>
                        );
                    })}

                    <Collapse in={shouldShowEmptyState} timeout={EXIT_DURATION}>
                        <Box sx={{ py: '32px', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '13px', color: '#8c8c8c' }}>
                                No pending invitations
                            </Typography>
                        </Box>
                    </Collapse>
                </Box>
            </Popover>

            {declineModalOpen && (
                <DeclineInvitationModal
                    open={declineModalOpen}
                    onClose={() => setDeclineInvite(null)}
                    onConfirm={handleConfirmDecline}
                    propertyName={declineInvite?.property_address || 'this property'}
                    loading={processingById[declineInvite?.invite_id] === 'decline'}
                />
            )}
        </>
    );
}
